<template>
  <div class="v3-form adult-form">
    <div class="row">
      <div>
        <LazyLoadSelect
          :is-multiple="true"
          class="flex-1 me-2"
          :selected="state.form.adults"
          type="adults"
          :added-options="addedAdults"
          placeholder="Select adults"
          @update:model-value="(value) => updateData(value)"
        />
        <div class="info-box mt-4">
          <InfoBox
            message="Specify teachers/staff to be allowed to keep pass transparency."
          />
        </div>
      </div>
      <div>
        <InfoBox
          v-if="state.serverRes"
          class="mt-4"
          :class="{ danger: Object.keys(state.serverErrors).length }"
          :title="state.serverRes.title"
          >{{ state.serverRes.message }}</InfoBox
        >
      </div>
      <div class="text-center">
        <LoadingButton class="me-2 px-4" @click="onCancel" rounded
          >Cancel</LoadingButton
        >
        <LoadingButton
          :is-loading="state.isProcessing"
          @click="allowAdults()"
          class="mt-4 px-4"
          solid
          rounded
          >Allow</LoadingButton
        >
      </div>
    </div>
  </div>
</template>

<script>
import LoadingButton from "@/v3components/shared/Buttons/LoadingButton.vue"
import LazyLoadSelect from "@/v3components/shared/Form/LazyLoadSelect.vue"
import InfoBox from "@/v3components/shared/Alerts/InfoBox.vue"
import useVuelidate from "@vuelidate/core"
import { helpers, required } from "@vuelidate/validators"
import { useStore } from "vuex"
import { reactive, computed, onMounted, ref } from "vue"

export default {
  name: "PassTransparencyForm",
  components: {
    LoadingButton,
    InfoBox,
    LazyLoadSelect
  },
  emits: ["cancel"],
  setup(props, { emit }) {
    const store = useStore()
    const state = reactive({
      isProcessing: false,
      serverErrors: {},
      serverRes: null,
      form: {
        adults: null
      }
    })

    const addedAdults = ref([])

    const validationMessages = {
      required: "This field is required"
    }
    const validations = {
      form: {
        adults: {
          required: helpers.withMessage(validationMessages.required, required)
        }
      }
    }
    const v$ = useVuelidate(validations.form, state.form)

    const isFormValid = computed(() => !v$.value.$invalid)

    const errors = computed(() => {
      const errorObj = {}
      v$.value.$errors.forEach((err) => {
        errorObj[err.$property] = err.$message
      })

      return errorObj
    })

    const updateData = (value) => {
      state.form.adults = value
      state.serverErrors = {}
      state.serverRes = null
    }

    const allowAdults = () => {
      state.isProcessing = true

      // Extract just the IDs from the selected adult objects
      const selectedIds = Array.isArray(state.form.adults)
        ? state.form.adults.map((adult) => adult.value?.id || adult.id || adult)
        : [
            state.form.adults.value?.id ||
              state.form.adults.id ||
              state.form.adults
          ]

      store
        .dispatch("passes/createTransparency", { user_ids: selectedIds })
        .then(() => {
          resetFormData()
          setResponseInfoBox("Success", "Successfully created!")
          state.isProcessing = false
          state.serverErrors = {}
          setTimeout(() => {
            setResponseInfoBox()
            store.dispatch("passes/getTransparencies")
            emit("cancel")
          }, 1000)
        })
        .catch((err) => {
          const res = err.response.data
          state.serverErrors = res.errors ? res.errors : {}
          setResponseInfoBox("Error", res.message)
          state.isProcessing = false
        })
    }

    const setResponseInfoBox = (title, message) => {
      if (title || message) {
        state.serverRes = {
          message,
          title
        }
      } else {
        state.serverRes = null
      }
    }

    const onCancel = () => {
      resetFormData()
      emit("cancel")
    }

    const resetFormData = () => {
      state.form.adults = null
    }

    onMounted(() => {
      store
        .dispatch("passes/getTransparencyUserIds")
        .then((resp) => {
          const result = resp?.data?.data?.transparencies || []
          addedAdults.value = result.filter(Boolean).map((adult) => {
            if (typeof adult === "object" && adult !== null) {
              const firstName =
                adult.first_name || adult.firstName || adult.first_name || ""
              const lastName =
                adult.last_name || adult.lastName || adult.last_name || ""
              const email = adult.email || ""

              let label = ""
              if (firstName || lastName) {
                label = `${firstName} ${lastName}`.trim()
                if (email) {
                  label += `, ${email}`
                }
              } else if (email) {
                label = email
              } else {
                label = `Adult ID: ${adult.id}`
              }

              return {
                label: label,
                value: {
                  id: adult.id,
                  type: "App\\Models\\User"
                }
              }
            } else {
              return {
                label: `Adult ID: ${adult}`,
                value: {
                  id: adult,
                  type: "App\\Models\\User"
                }
              }
            }
          })
        })
        .catch(() => {
          addedAdults.value = []
        })
    })

    return {
      state,
      v$,
      updateData,
      allowAdults,
      isFormValid,
      errors,
      onCancel,
      resetFormData,
      setResponseInfoBox,
      addedAdults
    }
  }
}
</script>
